import React from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "./NavBar";

export default function PrivacyPage({ user, onLogout }) {
  const navigate = useNavigate();
  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 800, margin: "0 auto", padding: "32px 20px" },
    backBtn: { background: "transparent", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, marginBottom: 24, padding: 0 },
    hero: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 20, padding: "32px 36px", marginBottom: 32, backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(59,130,246,0.08)" },
    h1: { fontSize: 32, fontWeight: 800, color: "#0f172a", marginBottom: 8 },
    meta: { fontSize: 14, color: "#64748b", marginBottom: 0 },
    section: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 16, padding: "24px 28px", marginBottom: 16, backdropFilter: "blur(8px)", boxShadow: "0 2px 8px rgba(59,130,246,0.06)" },
    h2: { fontSize: 18, fontWeight: 700, color: "#1e293b", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid rgba(59,130,246,0.08)" },
    h3: { fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 8, marginTop: 16 },
    p: { fontSize: 14, color: "#475569", lineHeight: 1.8, marginBottom: 12 },
    ul: { fontSize: 14, color: "#475569", lineHeight: 1.8, paddingLeft: 20, marginBottom: 12 },
    li: { marginBottom: 6 },
    contact: { background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 12, padding: "16px 20px", marginTop: 8 },
  };

  return (
    <div style={s.root}>
      {user ? <NavBar user={user} onLogout={onLogout} /> : (
        <nav style={{ padding: "16px 40px", borderBottom: "1px solid rgba(59,130,246,0.15)", background: "rgba(238,242,251,0.95)", backdropFilter: "blur(12px)" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", cursor: "pointer" }} onClick={() => navigate("/")}>
            i<span style={{ color: "#3b82f6" }}>Trust Secure</span>
          </div>
        </nav>
      )}
      <div style={s.main}>
        <button style={s.backBtn} onClick={() => navigate(-1)}>← Back</button>
        <div style={s.hero}>
          <div style={s.h1}>Privacy Policy</div>
          <div style={s.meta}>iTrust Secure LLC · itrustsecure.com · Effective Date: May 12, 2026 · Last Updated: May 12, 2026</div>
        </div>

        <div style={s.section}>
          <div style={s.h2}>1. Introduction</div>
          <p style={s.p}>iTrust Secure LLC ("iTrust," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use the iTrust platform at itrustsecure.com or our mobile applications. By using the Platform, you consent to the practices described in this policy.</p>
        </div>

        <div style={s.section}>
          <div style={s.h2}>2. Information We Collect</div>
          <div style={s.h3}>2.1 Information You Provide</div>
          <p style={s.p}>We collect information you provide directly to us, including:</p>
          <ul style={s.ul}>
            <li style={s.li}>Full name and email address during registration</li>
            <li style={s.li}>Password (stored in encrypted form using bcrypt)</li>
            <li style={s.li}>Phone number (optional)</li>
            <li style={s.li}>Listing information, photos, offer details, and transaction records</li>
            <li style={s.li}>Dispute information, evidence photos, and communications</li>
            <li style={s.li}>Invoice and payment request details</li>
            <li style={s.li}>Any other information you choose to provide</li>
          </ul>
          <div style={s.h3}>2.2 Information Collected Automatically</div>
          <p style={s.p}>When you use the Platform, we automatically collect:</p>
          <ul style={s.ul}>
            <li style={s.li}>IP address and device information</li>
            <li style={s.li}>Browser type and operating system</li>
            <li style={s.li}>Pages visited and actions taken on the Platform</li>
            <li style={s.li}>Date and time of access</li>
            <li style={s.li}>Push notification tokens (for mobile app users)</li>
          </ul>
          <div style={s.h3}>2.3 Payment Information</div>
          <p style={s.p}>Payment information is processed by Braintree (a PayPal service), our third-party payment processor. iTrust does not store full credit card numbers or sensitive payment data. Braintree's privacy policy governs the collection and use of your payment information.</p>
        </div>

        <div style={s.section}>
          <div style={s.h2}>3. How We Use Your Information</div>
          <p style={s.p}>We use the information we collect to:</p>
          <ul style={s.ul}>
            <li style={s.li}>Create and manage your account</li>
            <li style={s.li}>Process transactions and hold funds in escrow</li>
            <li style={s.li}>Facilitate communication between buyers and sellers</li>
            <li style={s.li}>Resolve disputes and investigate fraud</li>
            <li style={s.li}>Send transactional emails and platform notifications</li>
            <li style={s.li}>Send push notifications to mobile app users</li>
            <li style={s.li}>Improve and optimize the Platform</li>
            <li style={s.li}>Comply with legal obligations</li>
            <li style={s.li}>Enforce our Terms of Service</li>
          </ul>
        </div>

        <div style={s.section}>
          <div style={s.h2}>4. How We Share Your Information</div>
          <p style={s.p}>We do not sell your personal information. We may share your information with:</p>
          <ul style={s.ul}>
            <li style={s.li}><strong>Braintree (PayPal)</strong> — payment processing</li>
            <li style={s.li}><strong>Dwolla</strong> — ACH bank transfers and seller payouts</li>
            <li style={s.li}><strong>Resend</strong> — transactional email delivery</li>
            <li style={s.li}><strong>Cloudinary</strong> — photo and media storage</li>
            <li style={s.li}><strong>Expo</strong> — mobile push notification delivery</li>
            <li style={s.li}><strong>Railway</strong> — backend hosting and database infrastructure</li>
            <li style={s.li}><strong>Vercel</strong> — frontend web hosting</li>
            <li style={s.li}>Law enforcement or government agencies when required by law</li>
            <li style={s.li}>Other users only to the extent necessary to facilitate a transaction</li>
          </ul>
          <p style={s.p}>We do not share your personal information with advertisers or third parties for marketing purposes.</p>
        </div>

        <div style={s.section}>
          <div style={s.h2}>5. Data Security</div>
          <p style={s.p}>We implement industry-standard security measures to protect your personal information, including:</p>
          <ul style={s.ul}>
            <li style={s.li}>Encryption of passwords using bcrypt hashing</li>
            <li style={s.li}>Secure HTTPS connections for all data transmission</li>
            <li style={s.li}>JWT-based authentication with token expiration</li>
            <li style={s.li}>Regular security monitoring and updates</li>
          </ul>
          <p style={s.p}>Despite these measures, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your data.</p>
        </div>

        <div style={s.section}>
          <div style={s.h2}>6. Data Retention</div>
          <p style={s.p}>We retain your personal information for as long as your account is active or as needed to provide services. We may retain certain information for longer periods as required by law or for legitimate business purposes such as fraud prevention and dispute resolution.</p>
        </div>

        <div style={s.section}>
          <div style={s.h2}>7. Your Rights</div>
          <p style={s.p}>Depending on your location, you may have the following rights regarding your personal information:</p>
          <ul style={s.ul}>
            <li style={s.li}><strong>Access</strong> — Request a copy of the personal information we hold about you</li>
            <li style={s.li}><strong>Correction</strong> — Request correction of inaccurate or incomplete information</li>
            <li style={s.li}><strong>Deletion</strong> — Request deletion of your personal information, subject to legal obligations</li>
            <li style={s.li}><strong>Objection</strong> — Object to certain processing of your personal information</li>
            <li style={s.li}><strong>Portability</strong> — Request transfer of your data in a machine-readable format</li>
          </ul>
          <p style={s.p}>To exercise any of these rights, please contact us at founder@itrustsecure.com.</p>
        </div>

        <div style={s.section}>
          <div style={s.h2}>8. Cookies</div>
          <p style={s.p}>The Platform uses localStorage for session management and authentication tokens. We do not use third-party advertising cookies. You can clear stored data through your browser settings, though this will log you out of the Platform.</p>
        </div>

        <div style={s.section}>
          <div style={s.h2}>9. Children's Privacy</div>
          <p style={s.p}>The Platform is not directed to children under 18 years of age. We do not knowingly collect personal information from children. If we become aware that a child under 18 has provided us with personal information, we will delete it immediately.</p>
        </div>

        <div style={s.section}>
          <div style={s.h2}>10. Changes to This Policy</div>
          <p style={s.p}>We may update this Privacy Policy from time to time. We will notify you of material changes by email or by posting a notice on the Platform. Your continued use of the Platform after changes constitutes acceptance of the updated policy.</p>
        </div>

        <div style={s.section}>
          <div style={s.h2}>11. Contact Us</div>
          <div style={s.contact}>
            <p style={{ ...s.p, marginBottom: 4 }}><strong>iTrust Secure LLC</strong></p>
            <p style={{ ...s.p, marginBottom: 4 }}>Email: founder@itrustsecure.com</p>
            <p style={{ ...s.p, marginBottom: 4 }}>Website: itrustsecure.com</p>
            <p style={{ ...s.p, marginBottom: 0 }}>Authorized Representative: Samuel NanaYaw Afunyah Blankson</p>
          </div>
        </div>
      </div>
    </div>
  );
}